/**
 * Hub's binding of the shared error-message extraction in `@wtc/api-errors`.
 *
 * Sits next to `user-facing-errors.ts` on purpose — they are the two halves of
 * "a Postgres/Supabase error reached us, now what":
 *
 *   `mapToUserFacingError`  → what the CLIENT is allowed to see (safe, generic)
 *   `errorMessage` (here)   → what the LOG, the alert and the model see (exact)
 *
 * WHY IT IS NOT `error instanceof Error ? error.message : String(error)`:
 * a PostgREST failure is a PLAIN OBJECT, so `instanceof Error` is false and
 * `String(it)` is the literal `"[object Object]"`. `(error as Error).message`
 * is worse — a cast is not a check, so it logs `undefined`. And `.message`
 * alone is not enough: PostgREST leaves it empty on several failures and puts
 * the reason in `details`/`hint`.
 *
 * NOT the same thing as `getSafeErrorMessage` in `lib/utils/misc/safe-error`.
 * That one is a USER-facing sanitizer and is deliberately lossy — per
 * USER_FACING_API_ERRORS a client must never see a raw database message. These
 * are for the places nobody but us reads.
 *
 * @see docs/engineering-rules/USER_FACING_API_ERRORS.md
 * @see .claude/rules/NO_SWALLOWED_ERRORS.md
 */

import { extractErrorMessage } from "@wtc/api-errors";

export { extractErrorCode, describeError, isPostgrestLikeError } from "@wtc/api-errors";

/** The failure's message, reading `message → details → hint → code`. */
export function errorMessage(error: unknown): string {
  return extractErrorMessage(error);
}

/**
 * The stack, when there is one. Replaces `(error as Error).stack`, which is
 * `undefined` for every non-Error while looking like it read something.
 */
export function errorStack(error: unknown): string | undefined {
  return error instanceof Error ? error.stack : undefined;
}

/** The error's constructor name, when the value really is an `Error`. */
export function errorName(error: unknown): string | undefined {
  return error instanceof Error ? error.name : undefined;
}

/** Message + the stack/name fields, for a structured log record. */
export function errorInfo(error: unknown): {
  message: string;
  stack?: string;
  name?: string;
} {
  return {
    message: errorMessage(error),
    ...(error instanceof Error ? { stack: error.stack, name: error.name } : {}),
  };
}
